import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { NumericFormat } from 'react-number-format'

import Checkbox from '../../../../../../components/form/Checkbox'

import MoneyInput from '../../../../../../components/form/MoneyInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import { CurrencyList } from '../../../types'

import {
    PerformanceContractDocPositionDraft,
    PerformanceContractDocPositionItem,
    CountryList,
    Country,
    CountryOfOrigin,
    ContractPerformanceAcceptanceList,
    ContractPerformancePaymentAcceptanceSet,
} from '../types'

import HoverTooltip from '../../../../../../components/HoverTooltip'

type Props = {
    theme: string
    contractGuid: string | undefined
    handlePerformancePaymentsAcceptancePopup: () => void
    onSave: (items: ContractPerformancePaymentAcceptanceSet[]) => void
}

const PerformancePaymentsAcceptancePopup: React.FC<Props> = (props) => {
    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [allAcceptance, setAllAcceptance] = useState<boolean>(false)

    const [acceptanceList, setAcceptanceList] = useState<
        ContractPerformanceAcceptanceList[]
    >([])
    const [filteredAcceptanceList, setFilteredAcceptanceList] = useState<
        ContractPerformanceAcceptanceList[]
    >([])

    const [isLoadingPerformanceAcceptance, setLoadingPerformanceAcceptance] =
        useState(false)

    const getAcceptanceList = async (
        contract_guid: string
    ): Promise<ContractPerformanceAcceptanceList[]> => {
        setLoadingPerformanceAcceptance(true)
        return fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/performance_contract_docs_with_acceptance?contract_guid=${contract_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на Acceptance')
                }
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                setLoadingPerformanceAcceptance(false)
            })
    }

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(guid)) {
                return prevSelectedItems.filter((itemId) => itemId !== guid)
            } else {
                return [...prevSelectedItems, guid]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(guid)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, guid] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    const handleSelectAllChange = () => {
        setSelectAll(!selectAll)
        if (!selectAll) {
            // Выбираем только те элементы, которые соответствуют текущей стадии
            const filteredItems = filteredAcceptanceList
                ? filteredAcceptanceList.map((item) => item.guid)
                : []

            setSelectedItems(filteredItems)
        } else {
            // Сбрасываем выбор всех элементов
            setSelectedItems([])
        }
    }

    // Функция для обработки изменения состояния отдельного checkbox
    const handleAllAcceptanceChange = () => {
        if (allAcceptance == false) {
            const falseIsAcceptance = filteredAcceptanceList.filter(
                (itemId) => itemId.is_acceptence == false
            )
            const falseSelected = falseIsAcceptance
                ? falseIsAcceptance.map((item) => item.guid)
                : []
            setSelectedItems(
                selectedItems.filter((item) => falseSelected.includes(item))
            )
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (props.contractGuid) {
                    const acceptances = await getAcceptanceList(
                        props.contractGuid
                    )
                    if (Array.isArray(acceptances)) {
                        if (allAcceptance == false) {
                            setAcceptanceList(acceptances)
                            setFilteredAcceptanceList(
                                acceptances.filter(
                                    (itemId) =>
                                        itemId.is_acceptence == allAcceptance
                                )
                            )
                        } else {
                            setFilteredAcceptanceList(acceptances)
                        }
                    } else {
                        console.error('Нет данных:', acceptances)
                    }
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    useEffect(() => {
        const fetchData = async () => {
            if (acceptanceList) {
                if (allAcceptance == false) {
                    setFilteredAcceptanceList(
                        acceptanceList.filter(
                            (itemId) => itemId.is_acceptence == allAcceptance
                        )
                    )
                } else {
                    setFilteredAcceptanceList(acceptanceList)
                }
            } else {
                console.error('Нет данных')
            }
        }
        fetchData()
    }, [allAcceptance])

    const handleSave = () => {
        const selected = filteredAcceptanceList.filter((item) =>
            selectedItems.includes(item.guid)
        )

        const acceptances: ContractPerformancePaymentAcceptanceSet[] =
            selected.map((item) => ({
                guid: null, // или item.acceptance_guid, если есть
                guid_completing_info: item.performance_contract_guid, // guid строки/позиции
                position_ordinal: item.position_ordinal, // подгони под свой тип
                doc_ordinal: item.ordinal,
                acceptance_doc_name: item.performance_contract_doc_name,
                acceptance_doc_number: item.performance_contract_doc_number,
                acceptance_doc_date: item.performance_contract_doc_date,
                annotation: null,
                contract_position_name: item.contract_position_name,
                contract_position_okpd2_code: item.contract_position_okpd2_code,
                contract_position_okpd2_name: item.contract_position_okpd2_name,
                okei_name: item.okei_name,
                qty: item.qty,
                unit_price: item.unit_price,
                currency_code: item.currency_code,
                currency_digital_code: item.currency_digital_code,
                currency_name: item.currency_name,
                exchange_rate: item.exchange_rate,
                exchange_rate_date: item.exchange_rate_date,
                rub_unit_price: item.rub_unit_price,
                prepayment: item.prepayment,
                total_rub: item.total_rub,
            }))

        props.onSave(acceptances)
        props.handlePerformancePaymentsAcceptancePopup()
    }

    return (
        <div
            className="fixed z-20 top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
            // onClick={props.handlePerformancePaymentsAcceptancePopup}
        >
            <div
                className="flex flex-col p-5 rounded-lg w-[1200px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-1 flex-row">
                    <div className="flex flex-1">
                        <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[16px]">
                            Документы
                        </h2>
                    </div>
                    <div className="flex flex-row gap-3 items-center justify-center cursor-pointer">
                        <div
                            className="text-[#181818] dark:text-[#FFFFFF] text-[12.8px] cursor-pointer"
                            onClick={() => {
                                setAllAcceptance(!allAcceptance)
                                setSelectAll(false)
                                handleAllAcceptanceChange()
                            }}
                        >
                            Показать все
                        </div>
                        <input
                            type="checkbox"
                            checked={allAcceptance}
                            onClick={() => {
                                setAllAcceptance(!allAcceptance)
                                setSelectAll(false)
                                handleAllAcceptanceChange()
                            }}
                        />
                    </div>
                </div>

                <div className="flex flex-col w-full h-fit items-center justify-center">
                    <div className="flex flex-row w-full gap-1 h-[40px]-1 py-3 px-2 items-center justify-center rounded-t-md text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex w-5 items-center justify-center ">
                            <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={handleSelectAllChange}
                            />
                        </div>
                        <div className="flex w-10 items-center justify-center "></div>
                        <div className="flex flex-1 items-center justify-center ">
                            Документ
                        </div>
                        <div className="flex w-32 items-center justify-center ">
                            Дата
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Наименование позиции
                        </div>
                        <div className="flex w-32 items-center justify-center ">
                            Количество
                        </div>
                        <div className="flex w-32 items-center justify-center ">
                            Сумма за единицу
                        </div>
                        <div className="flex w-32 items-center justify-center ">
                            Сумма
                        </div>
                        <div className="flex w-16 items-center justify-center text-center">
                            Связь с оплатой
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-[400px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto custom-scrollbar text-[12.8px] bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                        {isLoadingPerformanceAcceptance ? (
                            <div className="flex w-full h-full items-start justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            filteredAcceptanceList.map((item, index) => (
                                <div
                                    key={item.guid}
                                    className={
                                        selectedItems.includes(item.guid)
                                            ? 'flex w-full h-fit gap-1 p-1 rounded-md text-[11px] text-[#181818] dark:text-[#FFFFFF] bg-[#FFFFFF] hover:bg-[#FFFFFF] dark:bg-[#373538] dark:hover:bg-[#373538] items-center justify-center cursor-pointer'
                                            : 'flex w-full h-fit gap-1 p-1 rounded-md text-[11px] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538] items-center justify-center cursor-pointer'
                                    }
                                    onClick={() => {
                                        handleCheckboxChange(item.guid)
                                        setSelectAll(false)
                                    }}
                                >
                                    <div className="flex flex-row w-full gap-1 py-1 items-center justify-center text-[10px] ">
                                        <div className="flex w-5 items-center justify-center ">
                                            <input
                                                type="checkbox"
                                                checked={selectedItems.includes(
                                                    item.guid
                                                )}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                onChange={() => {
                                                    handleCheckboxChange(
                                                        item.guid
                                                    )
                                                    setSelectAll(false)
                                                }}
                                            />
                                        </div>
                                        <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            {index + 1}
                                        </div>
                                        <div className="flex flex-1 items-center justify-start text-left text-[#181818] dark:text-[#FFFFFF]">
                                            {`${item.performance_contract_doc_name} № ${item.performance_contract_doc_number}`}
                                        </div>
                                        <div className="flex w-32 items-center justify-center text-[#181818] dark:text-[#FFFFFF]">
                                            {item.performance_contract_doc_date
                                                ?.split('-')
                                                .reverse()
                                                .join('-')
                                                .replace(/[\-]/g, '.')}
                                        </div>
                                        <div className="flex flex-1 items-center justify-start text-left text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                            {item.contract_position_name}
                                        </div>
                                        <div className="flex w-32 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                            {item.qty}
                                        </div>
                                        <div className="flex w-32 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                            {item.unit_price}
                                        </div>
                                        <div className="flex w-32 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                            {(
                                                (item.qty ?? 0) *
                                                (item.unit_price ?? 0)
                                            ).toLocaleString('ru-RU', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </div>
                                        <div className="flex w-16 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            {item.is_acceptence && (
                                                <svg
                                                    className="w-4 h-4 items-center justify-center"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    id="plus"
                                                >
                                                    <path
                                                        fill={
                                                            props.theme ==
                                                            'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                        }
                                                        d="M10,17.55,8.23,19.27a2.47,2.47,0,0,1-3.5-3.5l4.54-4.55a2.46,2.46,0,0,1,3.39-.09l.12.1a1,1,0,0,0,1.4-1.43A2.75,2.75,0,0,0,14,9.59a4.46,4.46,0,0,0-6.09.22L3.31,14.36a4.48,4.48,0,0,0,6.33,6.33L11.37,19A1,1,0,0,0,10,17.55ZM20.69,3.31a4.49,4.49,0,0,0-6.33,0L12.63,5A1,1,0,0,0,14,6.45l1.73-1.72a2.47,2.47,0,0,1,3.5,3.5l-4.54,4.55a2.46,2.46,0,0,1-3.39.09l-.12-.1a1,1,0,0,0-1.4,1.43,2.75,2.75,0,0,0,.23.21,4.47,4.47,0,0,0,6.09-.22l4.55-4.55A4.49,4.49,0,0,0,20.69,3.31Z"
                                                    ></path>
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                <div className="flex flex-row text-right justify-end items-center gap-2">
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={props.handlePerformancePaymentsAcceptancePopup}
                    >
                        Отмена
                    </button>
                    <button
                        className={`flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs  ${selectedItems.length > 0 ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]' : 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#494849]'}`}
                        onClick={handleSave}
                        disabled={!(selectedItems.length > 0)}
                    >
                        Связать
                    </button>
                </div>
            </div>
        </div>
    )
}
export default PerformancePaymentsAcceptancePopup
